// -----------------------------------------------------------------------------
// Location: packages/@honeycomb/ui-core/src/components/ActionButton/ActionButton.tsx
// -----------------------------------------------------------------------------

import * as React from 'react';
import { ButtonBaseProps } from '../ButtonBase/ButtonBase';
import { ActionButtonColor, ActionButtonSize, ActionButtonVariant, ResponsiveStyleValue } from '../../theming';
import { Styler, StylerProps } from '../Styler';
import { TypographyBody } from '../Typography';
import { STATIC_STYLES, useDynamicStyles } from './ActionButton.styles';

export interface ActionButtonProps extends StylerProps, ButtonBaseProps {
    alignEdge?: 'start' | 'end' | 'none';
    children?: React.ReactNode;
    color?: ActionButtonColor;
    fullWidth?: boolean;

    /**
     * Visual variant of the action button.
     * @default default
     */
    variant?: ActionButtonVariant;

    /**
     * Size of the action button.
     * Supports responsive values.
     * @default m
     */
    size?: ResponsiveStyleValue<ActionButtonSize>;

    icon?: React.ReactElement | Array<React.ReactElement>;
    hideLabel?: ResponsiveStyleValue<boolean>;
    hideIcon?: ResponsiveStyleValue<boolean>;
}

/**
 * Action Buttons are low-emphasis buttons which allow users to perform actions
 * or make choices. They are typically placed in mastheads or toolbars.
 */
export function ActionButton(props: Readonly<ActionButtonProps>): React.JSX.Element {
    const {
        alignEdge = 'none',
        children: label,
        color = 'primary',
        disabled,
        fullWidth = false,
        variant = 'default',
        size = 'm',
        icon,
        hideLabel = false,
        hideIcon = false,
        ref,
        ...other
    } = props;

    const dynamicStyles = useDynamicStyles(color, hideLabel, hideIcon, size, variant);

    const cssDataAttributes = React.useMemo(() => {
        const attributes: Record<string, true | string> = {
            'data-focus-ring': true,
        };

        if (fullWidth) attributes['data-full-width'] = true;
        if (alignEdge !== 'none') attributes[`data-align-edge-${alignEdge}`] = true;
        if (variant !== 'default') attributes['data-variant'] = variant;

        return attributes;
    }, [alignEdge, fullWidth, variant]);

    return (
        <Styler
            ref={ref}
            button={true}
            baseStyles={[STATIC_STYLES, dynamicStyles]}
            disabled={disabled}
            {...cssDataAttributes}
            {...other}
        >
            {icon && <span data-icon={true}>{icon}</span>}
            {label && (
                <TypographyBody component="span" data-label={true}>
                    {label}
                </TypographyBody>
            )}
        </Styler>
    );
}
